import { TransportError } from 'ProdantixSdk/core/errors';
import { type FetchLike, resolveFetch, trimTrailingSlash } from 'ProdantixSdk/core/http';

/// The conversation-token header, kept distinct from `Authorization: Bearer`,
/// which carries the project key. The two credentials must never be swapped.
const TOKEN_HEADER = 'X-Prodantix-Conversation-Token';

export interface ChatTurn {
  id: string;
  authorKind: 'end_user' | 'agent' | 'ai' | 'system';
  authorId: string | null;
  body: string;
  createdAt: string;
  /// Who wrote an agent turn, as the messenger names them. Null on every
  /// end-user and AI turn, and on an agent turn by someone the project does not
  /// show, which draws as the generic agent label.
  authorName?: string | null;
  /// The picture beside that name: an edge path the client completes with
  /// `avatarUrl`, or an ID picture's own URL. Null draws the initials.
  authorAvatarUrl?: string | null;
  attachments?: ChatAttachment[];
}

export interface ChatUpload {
  id: string;
  name: string;
  contentType: string;
  bytes: number;
}

export interface ChatAttachment extends ChatUpload {
  url: string;
}

export interface FileResponseLike {
  ok: boolean;
  status: number;
  blob(): Promise<Blob>;
  text(): Promise<string>;
}

export type FileFetchLike = (url: string, init: { body?: Blob; headers: Record<string, string>; method: string }) => Promise<FileResponseLike>;

export interface ArticleHit {
  id: string;
  title: string;
  trail: string[];
  url: string;
  embedUrl: string;
  pageId: string;
  language: string;
}

export interface ArticleRef {
  pageId: string;
  language: string;
}

export type ArticleReaction = 'disappointed' | 'neutral' | 'smiley';

export interface ArticleCollection {
  title: string;
  articles: number;
}

export interface ChatThread {
  id: string;
  status: string;
  turns: ChatTurn[];
}

export interface ChatClientOptions {
  /// Public project key (`pdx_pub_…`); rides in `Authorization: Bearer`.
  apiKey: string;
  /// The EDGE host, e.g. `https://eu.edge.prodantix.com`. Not the ingest host,
  /// which this comment claimed for the whole life of the widget: ingest serves
  /// `/v1/events`, `/v1/identify` and `/v1/group` and has never served a
  /// conversation. The conversation routes sat on the console api until
  /// 2026-08-25, behind an origin allowlist no customer site could be in, so
  /// every call from a browser was refused before it was sent.
  host: string;
  /// The end user's stable id, recorded on the conversation. Never trusted to
  /// authorize a read — the token does that.
  distinctId: string;
  /// Persist the conversation handle so a reload resumes the same thread rather
  /// than opening a new one. Defaults to `localStorage` in a browser.
  storage?: ChatStorage;
  fetchImpl?: FetchLike;
  fileFetchImpl?: FileFetchLike;
  onError?: (error: unknown) => void;
  /// The visitor's languages in their own order, which the help centre reads
  /// search in.
  locale?: string;
}

/// A customer's claim about who is browsing, and the proof for it. The hash is
/// HMAC-SHA256 of `userId` under the project's signing secret, computed on THEIR
/// server: a page cannot compute it, which is the entire point. Sent together or
/// not at all, because an id with no proof is exactly the claim M2 stopped
/// believing.
export interface ChatIdentity {
  userId: string;
  userHash: string;
}

export interface ChatStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

/// Where the visitor was when they wrote. One project observes every product's
/// domain and one inbox serves them all, so an agent reading "the button does
/// nothing" needs the product and the page without asking. Sent with the turn,
/// never shown to the visitor: the console draws it.
export interface PageContext {
  host: string;
  path: string;
  title: string;
}

/// The stored handle: id AND token together. Persisting only the token would
/// leave a reload unable to build the thread's URL, and persisting them under
/// two keys risks one surviving without the other. One key, one JSON value.
const STORAGE_KEY = 'prodantix.chat.handle';

interface ChatHandle {
  conversationId: string;
  token: string;
}

function browserStorage(): ChatStorage | undefined {
  const store = (globalThis as { localStorage?: Storage }).localStorage;
  if (!store) return undefined;
  return {
    get: key => store.getItem(key),
    remove: key => store.removeItem(key),
    set: (key, value) => store.setItem(key, value),
  };
}

/// The transport half of the chat widget: open a thread, append a turn, load
/// the thread. No DOM, so it is unit-testable with an injected fetch and is
/// the seam the panel renders over. A failed call reports through `onError`
/// and resolves to a safe value; the widget never throws into the host page.
export class ChatClient {
  private readonly fetchImpl?: FetchLike;
  private readonly fileFetchImpl: FileFetchLike;
  private readonly host: string;
  private readonly storage?: ChatStorage;
  private handle: ChatHandle | null;
  private identity: ChatIdentity | null = null;
  private fromArticle: ArticleRef | null = null;

  constructor(private readonly options: ChatClientOptions) {
    this.fetchImpl = resolveFetch(options.fetchImpl);
    this.fileFetchImpl = options.fileFetchImpl ?? ((url, init) => fetch(url, init));
    this.host = trimTrailingSlash(options.host);
    this.storage = options.storage ?? browserStorage();
    this.handle = this.readHandle();
  }

  get hasThread(): boolean {
    return this.handle !== null;
  }

  /// A picture URL the wire carries, ready for an `<img>`. An edge path is
  /// completed with the host and the key, because an image load cannot set the
  /// bearer header; any other URL is already whole.
  avatarUrl(url: string): string {
    if (!url.startsWith('/')) return url;
    return `${this.host}${url}?api_key=${encodeURIComponent(this.options.apiKey)}`;
  }

  /// The socket handshake, or null while there is no thread to join a room for.
  /// The same two credentials the REST routes present, in the order
  /// `join_conversation_room` checks them: the project key resolves tenancy, the
  /// conversation token authorizes the thread.
  get socketAuth(): { conversationId: string; conversationToken: string; projectKey: string } | null {
    if (!this.handle) return null;
    return { conversationId: this.handle.conversationId, conversationToken: this.handle.token, projectKey: this.options.apiKey };
  }

  /// Records the claim a page made. Held rather than sent immediately: a thread
  /// is what a person is attached TO, and a visitor who has not opened one yet
  /// has nothing to attach. The next `ensureThread` carries it.
  identify(identity: ChatIdentity): void {
    this.identity = identity;
  }

  /// Opens a thread and remembers its handle. A held handle is reused, so this
  /// is idempotent for a returning visitor: opening never abandons the thread
  /// they already have.
  async ensureThread(subject?: string): Promise<boolean> {
    if (this.handle) return true;
    const opened = await this.request<{ conversationId?: string; token?: string }>('POST', '/v1/conversations', {
      body: {
        distinctId: this.options.distinctId,
        fromArticle: this.fromArticle,
        subject,
        userHash: this.identity?.userHash,
        userId: this.identity?.userId,
      },
    });
    if (!opened?.conversationId || !opened.token) return false;
    this.handle = { conversationId: opened.conversationId, token: opened.token };
    this.fromArticle = null;
    this.storage?.set(STORAGE_KEY, JSON.stringify(this.handle));
    return true;
  }

  cameFrom(article: ArticleRef): void {
    this.fromArticle = { language: article.language, pageId: article.pageId };
  }

  async send(body: string, pageContext?: PageContext, attachmentIds?: string[]): Promise<ChatTurn | null> {
    if (!(await this.ensureThread())) return null;
    const handle = this.handle;
    if (!handle) return null;
    const result = await this.request<{ turn: ChatTurn }>('POST', `/v1/conversations/${encodeURIComponent(handle.conversationId)}/turns`, {
      body: { attachmentIds, body, pageContext },
      token: handle.token,
    });
    return result?.turn ?? null;
  }

  async upload(file: File): Promise<ChatUpload | null> {
    if (!(await this.ensureThread())) return null;
    const handle = this.handle;
    if (!handle) return null;
    return this.fileRequest('POST', `/v1/conversations/${encodeURIComponent(handle.conversationId)}/attachments?name=${encodeURIComponent(file.name)}`, handle.token, async response => JSON.parse(await response.text()) as ChatUpload, file);
  }

  async file(path: string): Promise<Blob | null> {
    const handle = this.handle;
    if (!handle) return null;
    return this.fileRequest('GET', path, handle.token, response => response.blob());
  }

  /// One read of the help centre on the edge, in the visitor's languages. The
  /// help centre's own script names the reads, so a page without article search
  /// downloads none of them.
  async helpCentre<T>(suffix: string, params: Record<string, string>, body?: Record<string, string>): Promise<T | null> {
    const query = new URLSearchParams({ ...params, ...(this.options.locale && { lang: this.options.locale }), ...(this.identity && { user_hash: this.identity.userHash, user_id: this.identity.userId }) }).toString();
    return this.request<T>(body ? 'POST' : 'GET', `/v1/messenger/articles${suffix}${query ? `?${query}` : ''}`, body ? { body: { ...body, distinctId: this.options.distinctId } } : {});
  }

  /// The visitor as the page proved them, for a kit whose reads answer only to
  /// a verified visitor; null until the page identifies them.
  get visitor(): (ChatIdentity & { distinctId: string }) | null {
    return this.identity && { ...this.identity, distinctId: this.options.distinctId };
  }

  /// One call a kit fetched on demand makes to the edge: a read without a body,
  /// a write with one, and a conversation's token when it acts on a thread.
  edge<T>(path: string, body?: unknown, token?: string): Promise<T | null> {
    return this.request<T>(body ? 'POST' : 'GET', path, { body, token });
  }

  async load(): Promise<ChatThread | null> {
    const handle = this.handle;
    if (!handle) return null;
    return this.request<ChatThread>('GET', `/v1/conversations/${encodeURIComponent(handle.conversationId)}`, {
      token: handle.token,
    });
  }

  private readHandle(): ChatHandle | null {
    const raw = this.storage?.get(STORAGE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<ChatHandle>;
      if (typeof parsed.conversationId === 'string' && typeof parsed.token === 'string') {
        return { conversationId: parsed.conversationId, token: parsed.token };
      }
    } catch (error) {
      // A corrupt handle is not resumable; drop it and let the next send open a
      // fresh thread rather than throwing on parse.
      this.options.onError?.(error);
    }
    this.storage?.remove(STORAGE_KEY);
    return null;
  }

  private async fileRequest<T>(method: string, path: string, token: string, read: (response: FileResponseLike) => Promise<T>, body?: Blob): Promise<T | null> {
    const headers: Record<string, string> = { authorization: `Bearer ${this.options.apiKey}`, [TOKEN_HEADER]: token };
    if (body) headers['content-type'] = body.type || 'application/octet-stream';
    try {
      const response = await this.fileFetchImpl(`${this.host}${path}`, { body, headers, method });
      if (response.ok) return await read(response);
      this.options.onError?.(new TransportError(`prodantix chat ${path}: ${response.status}`, response.status));
    } catch (error) {
      this.options.onError?.(error);
    }
    return null;
  }

  private async request<T>(method: string, path: string, opts: { body?: unknown; token?: string } = {}): Promise<T | null> {
    const fetchImpl = this.fetchImpl;
    if (!fetchImpl) {
      this.options.onError?.(new TransportError('no fetch available for prodantix chat'));
      return null;
    }
    const headers: Record<string, string> = {
      authorization: `Bearer ${this.options.apiKey}`,
      'content-type': 'application/json',
    };
    if (opts.token) {
      headers[TOKEN_HEADER] = opts.token;
    }
    try {
      const response = await fetchImpl(`${this.host}${path}`, {
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        headers,
        method,
      });
      const text = await response.text();
      if (!response.ok) {
        this.options.onError?.(new TransportError(`prodantix chat ${path}: ${response.status}`, response.status));
        return null;
      }
      return text ? (JSON.parse(text) as T) : ({} as T);
    } catch (error) {
      this.options.onError?.(error);
      return null;
    }
  }
}
